// Basic
import PropTypes from 'prop-types';
// Components
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import RenderSimOperator from '@/app/modules/sims/components/simOperator/RenderSimOperator';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import ICONS from '@/common-utils/enums/icons';
import getIccidWithoutPrefix, { ICCID_REGEX, ICCID_SPECIAL_REGEX } from '@/common-utils/functions/getIccidWithoutPrefix';
import NETWORK_INTERFACE_TYPE from '../../../enums/NetworkInterfaceType';
import logoethernet from 'url:../../../../../../../resource/images/logoethernet.png';
import logoPayludd from 'url:../../../../../../../resource/images/Logo_Payludd_N&B.png';

/**
 * @param root
 * @param root.connectionType
 * @param root.simNumber
 * @param root.iconOnly
 * @param root.iccidOperators
 * @param root.showDefaultOperatorMessage
 * @returns {JSX} RenderPosTerminalConnectionType
 */
function RenderPosTerminalConnectionType({
    connectionType,
    simNumber,
    iconOnly,
    iccidOperators,
    showDefaultOperatorMessage = true
}) {

    // Constants
    const typeToIcon = {
        GPRS: ICONS.GPRS,
        WIFI: ICONS.WIFI,
        PPP: ICONS.PPP,
        OTHER: ICONS.UNKNOWN,
        NOT_SPECIFIED: ICONS.UNKNOWN
    };
    const connectionTypeUpperCase = connectionType && connectionType.toUpperCase() || '';

    const isSimNumber = simNumber && (ICCID_REGEX.test(simNumber) || ICCID_SPECIAL_REGEX.test(simNumber));
    const otherCase =
        connectionTypeUpperCase !== NETWORK_INTERFACE_TYPE.ETHERNET.name &&
        connectionTypeUpperCase !== NETWORK_INTERFACE_TYPE.PAYLUDD.name &&
        connectionTypeUpperCase !== NETWORK_INTERFACE_TYPE.GPRS.name &&
        !simNumber;

    // Classname
    const triggerClass = 'cursor-default';

    return (
        <div className='flex flex-col text-center items-center'>
            {
                connectionTypeUpperCase === NETWORK_INTERFACE_TYPE.ETHERNET.name &&
                <TooltipHelper
                    triggerClass={triggerClass}
                    trigger={<img alt=''
                        className='w-8 h-8'
                        src={logoethernet}
                        data-cy='logo-ethernet'
                    />}
                    content={<Entity
                        entity={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase].entity}
                        namespace={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase].namespace}
                    />}
                />
            }
            {
                connectionTypeUpperCase === NETWORK_INTERFACE_TYPE.PAYLUDD.name &&
                <>
                    <TooltipHelper
                        triggerClass={triggerClass}
                        trigger={<img alt=''
                            className='w-6 h-6'
                            src={logoPayludd}
                            data-cy='logo-payludd'
                        />}
                        content={<Entity
                            entity={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase].entity}
                            namespace={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase].namespace}
                        />}
                    />
                    <small>
                        <Entity nowrap entity={NETWORK_INTERFACE_TYPE.PAYLUDD.entity} namespace={NETWORK_INTERFACE_TYPE.PAYLUDD.namespace} />
                    </small>
                </>
            }
            {
                connectionTypeUpperCase === NETWORK_INTERFACE_TYPE.GPRS.name &&
                <>
                    {
                        iconOnly ?
                            <TooltipHelper
                                triggerClass={triggerClass}
                                trigger={<Icon
                                    icon={typeToIcon[connectionTypeUpperCase]}
                                />}
                                content={<Entity
                                    entity={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase].entity}
                                    namespace={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase].namespace}
                                />}
                            />
                            :
                            <div className='containerIconSimNumber'>
                                <div className='item'>
                                    <TooltipHelper
                                        triggerClass={triggerClass}
                                        trigger={<Icon
                                            icon={typeToIcon[connectionTypeUpperCase]}
                                        />}
                                        content={<Entity
                                            entity={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase].entity}
                                            namespace={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase].namespace}
                                        />}
                                    />
                                </div>
                                {
                                    simNumber &&
                                    <small className='item'>
                                        {isSimNumber ? getIccidWithoutPrefix(simNumber) : simNumber}
                                    </small>
                                }
                            </div>
                    }
                    {
                        simNumber &&
                        <RenderSimOperator
                            iccidOperators={iccidOperators}
                            showDefaultOperatorMessage={showDefaultOperatorMessage}
                            className='h-6'
                        />
                    }
                </>
            }
            {
                otherCase &&
                <TooltipHelper
                    triggerClass={triggerClass}
                    trigger={<Icon
                        icon={typeToIcon[connectionTypeUpperCase]}
                    />}
                    content={<Entity
                        entity={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase]?.entity}
                        namespace={NETWORK_INTERFACE_TYPE[connectionTypeUpperCase]?.namespace}
                    />}
                />
            }
        </div>
    );
}

RenderPosTerminalConnectionType.propTypes = {
    connectionType: PropTypes.string,
    simNumber: PropTypes.string,
    iconOnly: PropTypes.bool,
    iccidOperators: PropTypes.array,
    showDefaultOperatorMessage: PropTypes.bool
};

export default RenderPosTerminalConnectionType;